import React, { useEffect, useRef } from 'react';
import { Button, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  buildInvalidOptionKey,
  buildNoDefaultOptionKey,
  buildNotOnMenuKey,
  buildQuantityEscalationKey,
  buildUnmatchedCouponKey,
  invalidOptionAnchorKey,
  noDefaultOptionAnchorKey,
  notOnMenuAnchorKey,
  quantityEscalationAnchorKey,
  unmatchedCouponAnchorKey,
  DISMISS_READBACK_TTS,
} from '../../redux/features/unresolvedItems/unresolvedItems.constants';
import {
  selectCappedQuantityFlagsByCartItemId,
  selectUnresolvedItemAnchors,
  selectUnresolvedItemsCount,
  selectVisibleInvalidOptionLines,
  selectVisibleNoDefaultOptionConstraints,
  selectVisibleNotOnMenuCards,
  selectVisibleQuantityEscalationItems,
  selectVisibleUnmatchedCoupons,
} from '../../redux/features/unresolvedItems/unresolvedItems.selector';
import {
  clearHighlightedTranscriptMessage,
  dismissAllUnresolvedItems,
  setHighlightedTranscriptMessage,
} from '../../redux/features/unresolvedItems/unresolvedItems.slice';
import { selectValidCartItems } from '../../selectors/cart';
import { selectDismissReadbackPhrases } from '../../selectors/menu';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';
import useTTSMessage from '../../hooks/useTTSMessage.hooks';
import useReadback from '../../hooks/useReadback/useReadback.hooks';
import Colors from '../../utils/color';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import {
  cappedCartItemSelector,
  getCappedQuantity,
  isCouponToken,
  isNotOnMenuToken,
  normalizeItemName,
  prettifyUnresolvedItemName,
  transcriptMessageDomId,
} from '../../utils/unresolvedItems';
import UnresolvedItemCard, {
  UnresolvedItemVariant,
} from './UnresolvedItemCard';

// How long the "Show in transcript" pulse stays armed before the highlight
// state clears (the CSS animation itself is shorter).
const TRANSCRIPT_HIGHLIGHT_MS = 3000;

const useStyles = makeStyles<Theme, { isHitl3: boolean }>(
  ({ spacing }: Theme) => ({
    container: {
      // HITL 3.0 tightens the panel's outer spacing to the shared 8px rhythm.
      marginBottom: ({ isHitl3 }) => (isHitl3 ? HITL3_PADDING : spacing(2)),
      paddingBottom: spacing(1),
      borderBottom: `${spacing(0.125)} solid ${Colors.alto}`,
    },
    header: {
      fontWeight: 'bold !important',
      color: `${Colors.internationalOrange} !important`,
    },
    buttonsWrapper: {
      display: 'flex',
      gap: spacing(1),
      marginTop: ({ isHitl3 }) => (isHitl3 ? HITL3_PADDING : spacing(2)),
    },
    actionButton: {
      flex: 1,
      textTransform: 'none !important' as any,
      fontWeight: 'bold !important',
    },
  })
);

/**
 * Per-session panel surfacing the items the AI flagged as unresolved
 * (NOT_ON_MENU, invalid options, quantity escalations, no-default-option
 * constraints, unmatched coupons) as diagnostic cards. When the agent clicks
 * "Dismiss & Readback", a natural TTS message is sent followed by a readback
 * request so the customer hears the current order, and the panel is dismissed.
 *
 * HITL-1621: every card carries the customer utterance that triggered it with a
 * "Show in transcript" jump; machine-token invalid options (e.g.
 * `not_on_menu_butterfinger_burger`) are re-presented as readable not-on-menu /
 * coupon cards; quantity cards click through to the capped cart item.
 */
export default function UnresolvedItemsPanel() {
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const { container, header, buttonsWrapper, actionButton } = useStyles({
    isHitl3,
  });
  const dispatch = useAppDispatch();
  const { sendTTSMessage } = useTTSMessage();
  const { handleReadBack } = useReadback();

  const notOnMenuCards = useAppSelector(selectVisibleNotOnMenuCards);
  const invalidOptionLines = useAppSelector(selectVisibleInvalidOptionLines);
  const quantityEscalationItems = useAppSelector(
    selectVisibleQuantityEscalationItems
  );
  const noDefaultOptionConstraints = useAppSelector(
    selectVisibleNoDefaultOptionConstraints
  );
  const unmatchedCoupons = useAppSelector(selectVisibleUnmatchedCoupons);
  const count = useAppSelector(selectUnresolvedItemsCount);
  const validCartItems = useAppSelector(selectValidCartItems);
  const hasCartItems = Object.keys(validCartItems).length > 0;
  const dismissReadbackPhrases = useAppSelector(selectDismissReadbackPhrases);
  const anchors = useAppSelector(selectUnresolvedItemAnchors);
  const cappedFlags = useAppSelector(selectCappedQuantityFlagsByCartItemId);

  // "Show in transcript" clears its own highlight after a delay; drop a pending
  // clear if the panel unmounts first (the clear is id-scoped, so this is just
  // timer hygiene).
  const highlightTimerRef = useRef<number>();
  useEffect(() => () => window.clearTimeout(highlightTimerRef.current), []);

  if (count === 0) {
    return null;
  }

  const handleDismissAndReadback = () => {
    const phrases = dismissReadbackPhrases ?? [DISMISS_READBACK_TTS];
    const message = phrases[Math.floor(Math.random() * phrases.length)];
    sendTTSMessage(message);
    handleReadBack({ timePressed: Date.now(), forceRegularReadback: true });
    dispatch(dismissAllUnresolvedItems());
  };

  const handleDismiss = () => {
    dispatch(dismissAllUnresolvedItems());
  };

  // Scroll the transcript to the anchored line and pulse it. The clear is
  // id-scoped in the reducer, so a newer pulse isn't cut short by an older
  // card's timeout.
  const handleShowInTranscript = (messageId: string) => {
    dispatch(setHighlightedTranscriptMessage(messageId));
    document
      .getElementById(transcriptMessageDomId(messageId))
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    highlightTimerRef.current = window.setTimeout(() => {
      dispatch(clearHighlightedTranscriptMessage(messageId));
    }, TRANSCRIPT_HIGHLIGHT_MS);
  };

  // The cart line the AI capped for this quantity item, if the selector
  // resolved the name to exactly one line (see
  // selectCappedQuantityFlagsByCartItemId — ambiguous names flag nothing).
  const cappedCartItemIdFor = (itemName: string): string | undefined => {
    const normalized = normalizeItemName(itemName);
    return Object.keys(cappedFlags).find(
      (cartItemId) => cappedFlags[cartItemId].itemName === normalized
    );
  };

  const handleScrollToCartItem = (cartItemId: string) => {
    document
      .querySelector(cappedCartItemSelector(cartItemId))
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const anchorProps = (anchorKey: string) => {
    const anchor = anchors[anchorKey];
    return {
      anchor,
      onShowInTranscript: anchor?.messageId
        ? () => handleShowInTranscript(anchor.messageId)
        : undefined,
    };
  };

  // HITL-1621: the producer routes most unmatched items down the invalid-option
  // lane as machine tokens. Partition them so tokens render as readable
  // not-on-menu / coupon cards and only genuine option problems keep the
  // invalid-option treatment.
  const tokenNotOnMenuLines = invalidOptionLines.filter((line) =>
    isNotOnMenuToken(line.option)
  );
  const tokenCouponLines = invalidOptionLines.filter((line) =>
    isCouponToken(line.option)
  );
  const genuineInvalidOptionLines = invalidOptionLines.filter(
    (line) => !isNotOnMenuToken(line.option) && !isCouponToken(line.option)
  );

  return (
    <div id="unresolvedItems" className={container}>
      <Typography variant="subtitle2" className={header}>
        Unresolved items ({count})
      </Typography>
      {notOnMenuCards.map((card, index) => (
        <UnresolvedItemCard
          key={`not-on-menu-${buildNotOnMenuKey(card)}-${index}`}
          variant={UnresolvedItemVariant.NOT_ON_MENU}
          title={prettifyUnresolvedItemName(card.item)}
          diagnosticLabel="couldn't resolve — not on menu"
          {...anchorProps(notOnMenuAnchorKey(card))}
        />
      ))}
      {tokenNotOnMenuLines.map((line, index) => (
        <UnresolvedItemCard
          key={`token-not-on-menu-${buildInvalidOptionKey(line)}-${index}`}
          variant={UnresolvedItemVariant.NOT_ON_MENU}
          title={prettifyUnresolvedItemName(line.option)}
          diagnosticLabel="couldn't resolve — not on menu"
          {...anchorProps(invalidOptionAnchorKey(line))}
        />
      ))}
      {tokenCouponLines.map((line, index) => (
        <UnresolvedItemCard
          key={`token-coupon-${buildInvalidOptionKey(line)}-${index}`}
          variant={UnresolvedItemVariant.UNMATCHED_COUPON}
          title="Coupon"
          diagnosticLabel="no matching offer in menu"
          {...anchorProps(invalidOptionAnchorKey(line))}
        />
      ))}
      {genuineInvalidOptionLines.map((line, index) => (
        <UnresolvedItemCard
          key={`invalid-option-${buildInvalidOptionKey(line)}-${index}`}
          variant={UnresolvedItemVariant.INVALID_OPTION}
          title={`"${line.option}" in ${
            line.is_top_level ? 'top-level' : line.constraint ?? 'top-level'
          }`}
          diagnosticLabel="invalid option"
          {...anchorProps(invalidOptionAnchorKey(line))}
        />
      ))}
      {quantityEscalationItems.map((item, index) => {
        const capped = getCappedQuantity(item);
        const cappedCartItemId = capped
          ? cappedCartItemIdFor(item.item_name)
          : undefined;
        return (
          <UnresolvedItemCard
            key={`qty-escalation-${buildQuantityEscalationKey(item)}-${index}`}
            variant={UnresolvedItemVariant.QUANTITY_ESCALATION}
            title={`"${item.item_name}" × ${item.quantity}`}
            diagnosticLabel={
              capped
                ? `capped at ${capped.cappedTo} in the cart (threshold ${item.threshold})`
                : `quantity exceeds threshold (${item.threshold})`
            }
            onCardClick={
              cappedCartItemId
                ? () => handleScrollToCartItem(cappedCartItemId)
                : undefined
            }
            {...anchorProps(quantityEscalationAnchorKey(item))}
          />
        );
      })}
      {noDefaultOptionConstraints.map((entry, index) => (
        <UnresolvedItemCard
          key={`no-default-${buildNoDefaultOptionKey(entry)}-${index}`}
          variant={UnresolvedItemVariant.NO_DEFAULT_OPTION}
          title={`"${entry.constraint}" on ${entry.parent_item}`}
          diagnosticLabel="no default option — needs agent pick"
          {...anchorProps(noDefaultOptionAnchorKey(entry))}
        />
      ))}
      {unmatchedCoupons.map((coupon, index) => (
        <UnresolvedItemCard
          key={`unmatched-coupon-${buildUnmatchedCouponKey(coupon)}-${index}`}
          variant={UnresolvedItemVariant.UNMATCHED_COUPON}
          title={coupon.coupon}
          diagnosticLabel="no matching offer in menu"
          {...anchorProps(unmatchedCouponAnchorKey(coupon))}
        />
      ))}
      <div className={buttonsWrapper}>
        <Button
          variant="outlined"
          color="warning"
          size="small"
          className={actionButton}
          onClick={handleDismissAndReadback}
          disabled={!hasCartItems}
        >
          Dismiss &amp; Readback
        </Button>
        <Button
          variant="contained"
          color="warning"
          size="small"
          className={actionButton}
          onClick={handleDismiss}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}
